import crypto from "crypto";

import { useApolloClient } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { useState, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import * as y from "yup";

import { Button } from "@/components/Buttons";
import {
  HiddenField,
  SingleCheckboxField,
} from "@/components/Form/Parts/index";
import {
  useSignupQuery,
  useAccountIdcTokenSignupMutation,
  useClearRecommendArticleMutation,
} from "@/hooks";
import routes from "@/lib/routes";
import yupAgeValidate from "@/lib/yupAgeValidate";
import yupPasswordValidate from "@/lib/yupPasswordValidate";
import formStyles from "@/styles/Form.module.scss";
import processStyles from "@/styles/Process.module.scss";

import ErrorMessage from "./Parts/ErrorMessage";
import RecommendArticleForm from "./RecommendArticleForm";
import Loading from "../Loading";

yupAgeValidate(y);
yupPasswordValidate(y);

const schema = y.object().shape({
  terms: y.boolean().oneOf([true], "規約に同意してください。"),
});

export default function SignupForm() {
  const { formState, register, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
  });
  const { isDirty, isValid, isSubmitting, errors } = formState;

  const { data, loading: termloading } = useSignupQuery({
    variables: {},
  });
  const { requiredTerms, optionalTerms, recommendArticle } = data ?? {};
  const courses = recommendArticle?.courses || [];
  const products = recommendArticle?.products;

  // バリュープランのみの場合はRecommendArticleFormを表示しない
  const hasRecommendArticle =
    (courses && courses.length === 1 && !courses[0]?.valuePlan) || products;

  const [recommend, setRecommend] = useState();
  const [submited, setSubmited] = useState(false);

  const client = useApolloClient();

  const [signup, { error: signupError }] = useAccountIdcTokenSignupMutation();
  const [clearRecommendArticle] = useClearRecommendArticleMutation();

  const onSubmit = useCallback(
    async (data, recommend) => {
      return new Promise(async (resolve, reject) => {
        try {
          const terms = (() => {
            let values = [];
            if (data.terms) {
              values.push(data.termsValue);
            }
            if (data.termsAd) {
              values.push(data.termsAdValue);
            }
            if (data.termsMail) {
              values.push(data.termsMailValue);
            }
            if (values.length > 0) {
              return values.join(",");
            } else {
              return null;
            }
          })();
          const signupParams = {
            ...data,
            terms,
            signature: uuidv4(),
            password: crypto.randomBytes(8).toString("hex"), //ランダムな文字列をセット
          };
          if (!submited) {
            setSubmited(true);
            await signup({ variables: signupParams });
            // 任意規約の表示を行ったのでAPIをコール
            await fetch("/api/optional_terms", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ confirm: true }),
            });
            await clearRecommendArticle();
            if (recommend?.type === "svod") {
              window.location.href = routes.svodPaymentAfterSignUp(
                recommend.id[0]
              );
            } else if (recommend?.type === "tvod") {
              window.location.href = routes.tvodPaymentAfterSignUp(
                recommend.id[0]
              );
            } else {
              window.location.href = routes.signupComplete;
            }
          }
        } catch (e) {
          if (e?.name !== "AbortError") {
            console.error("Signup error:", e);
          }
          setSubmited(false);
          reject();
        }
      });
    },
    [submited]
  );

  const relayOnSubmit = useCallback(
    (data) => {
      onSubmit(data, recommend);
    },
    [recommend, onSubmit]
  );

  const terms = useMemo(() => {
    if (requiredTerms) {
      const links = requiredTerms.map((term) => (
        <Link
          key={term.id_key}
          href={term.current_revision.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {term.display_name}
        </Link>
      ));
      const value = requiredTerms.map(
        (term) => term.current_revision.unique_key
      );
      return { links, value };
    } else {
      return {};
    }
  }, [requiredTerms]);

  const adTerm = useMemo(() => {
    if (optionalTerms) {
      const term = optionalTerms.find((term) => term.id_key === "ad");
      if (term) {
        return {
          link: term.current_revision.url,
          value: term.current_revision.unique_key,
        };
      }
    }
    return null;
  }, [optionalTerms]);

  const mailTerm = useMemo(() => {
    if (optionalTerms) {
      const key = "spoox_mail";
      const term = optionalTerms.find((term) => term.id_key === key);
      if (term) {
        return {
          link: term.current_revision.url,
          value: term.current_revision.unique_key,
        };
      }
    }
    return null;
  }, [optionalTerms]);

  return (
    <form onSubmit={handleSubmit(relayOnSubmit)} noValidate>
      {termloading && <Loading fixed />}
      {!termloading && (
        <div className={processStyles.contentWrapper}>
          <div className={processStyles.content}>
            {terms && (
              <fieldset className={formStyles.basicFieldset}>
                <SingleCheckboxField
                  {...register("terms")}
                  title="規約の確認と同意"
                  name="terms"
                  defaultChecked={false}
                  label={
                    <p className={formStyles.terms}>
                      {terms.links}を確認の上、同意する
                      <br />
                      ※本会員サービス契約、および、本サービス契約は、個人からの申込みに限り受付けるものとし、法人、その他社団・組合等の団体からの申込みは受付けず、これらの者との間では、本会員サービス契約は有効に成立しないものとします。
                    </p>
                  }
                  error={errors.terms?.message}
                  required
                />
                <HiddenField
                  {...register("termsValue")}
                  defaultValue={terms.value}
                />
              </fieldset>
            )}
            {adTerm && (
              <fieldset className={formStyles.basicFieldset}>
                <SingleCheckboxField
                  {...register("termsAd")}
                  title="広告におけるデータ利用の確認と同意"
                  defaultChecked={false}
                  label={
                    <p className={formStyles.terms}>
                      本サービスの利用状況等をお客様の趣味・嗜好等に合致した情報の提供等に利用することに同意する(詳細は
                      <Link
                        href={adTerm.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        こちら
                      </Link>
                      )
                    </p>
                  }
                  error={errors.termsAd?.message}
                />
                <HiddenField
                  {...register("termsAdValue")}
                  name="termsAdValue"
                  defaultValue={adTerm.value}
                />
              </fieldset>
            )}
            {mailTerm && (
              <fieldset className={formStyles.basicFieldset}>
                <SingleCheckboxField
                  {...register("termsMail")}
                  title="ご案内メールへの同意"
                  defaultChecked={false}
                  label={
                    <p className={formStyles.terms}>
                      本サービスからのご案内メールの送付に同意する(詳細は
                      <Link
                        href={mailTerm.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        こちら
                      </Link>
                      )
                    </p>
                  }
                  error={errors.terms_mail?.message}
                />
                <HiddenField
                  {...register("termsMailValue")}
                  defaultValue={mailTerm.value}
                />
              </fieldset>
            )}
          </div>
        </div>
      )}
      {signupError && <ErrorMessage msg={signupError?.message} />}
      <RecommendArticleForm
        recommendArticle={recommendArticle}
        onChange={setRecommend}
      />
      <div className={formStyles.btnArea}>
        <Button
          type="submit"
          fillColor
          large
          fontFixed
          disabled={!isDirty || !isValid}
          disabledWithGray
          loading={submited || isSubmitting}
        >
          {!hasRecommendArticle && "利用登録する"}
          {hasRecommendArticle && "利用登録完了し、購入手続きへ"}
        </Button>
      </div>
    </form>
  );
}
